
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SaveDraftButtonProps {
  content: string;
  wordCount: number;
  draftId: string | null;
  userId: string | undefined;
  topicId: string;
  setDraftId: (id: string) => void;
}

export const SaveDraftButton = ({
  content,
  wordCount,
  draftId,
  userId,
  topicId,
  setDraftId,
}: SaveDraftButtonProps) => {
  const handleManualSave = async () => {
    if (!userId || !topicId) {
      console.error("[ManualSave] Missing required fields:", { userId, topicId });
      toast.error("Please select a topic to save this draft.");
      const topicSelector = document.querySelector('[data-topic-selector="true"]');
      if (topicSelector) {
        topicSelector.classList.add('animate-shake');
        setTimeout(() => {
          topicSelector.classList.remove('animate-shake');
        }, 3000);
      }
      return;
    }

    console.log("[ManualSave] Starting manual save...", { userId, topicId });

    try {
      if (draftId) {
        console.log("[ManualSave] Updating existing draft:", draftId);
        const { error } = await supabase
          .from("drafts")
          .update({
            content,
            word_count: wordCount,
            updated_at: new Date().toISOString(),
          })
          .eq("id", draftId);

        if (error) throw error;
      } else {
        console.log("[ManualSave] Creating new draft");
        const { data, error } = await supabase
          .from("drafts")
          .insert({
            user_id: userId,
            topic_id: topicId,
            content,
            word_count: wordCount,
          })
          .select()
          .single();

        if (error) throw error;
        setDraftId(data.id);
      }

      toast.success("Draft saved successfully");
      console.log("[ManualSave] Save successful");
    } catch (error) {
      console.error("[ManualSave] Save failed:", error);
      toast.error("Failed to save draft");
    }
  };

  return (
    <Button 
      onClick={handleManualSave} 
      variant="outline"
      className="dark:bg-stone-800 dark:text-white dark:border-stone-700 dark:hover:bg-stone-700"
    >
      <Save className="mr-2 h-4 w-4" />
      Save Draft
    </Button>
  );
};
